<template>
  <v-dialog v-model="shown" max-width="600">
    <v-card v-if="invoice">
      <v-card-text v-if="loading" style="height: 241px; padding: 1px">
        <AppSpinner />
      </v-card-text>
      <v-card-text v-else>
        <v-row class="preview">
          <InvoicePreviewDuplicate
            style="margin-top: 20px;"
            v-for="(item, i) in duplicateInvoices" :key="i"
            class="invoice-component"
            :invoice="item"
            @keep="keep(item)"
            @cancel="cancel(item)"
          />
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import BigNumber from "bignumber.js";
import moment from "moment";
import InvoicePreviewDuplicate from "./InvoicePreviewDuplicate";
import { mapActions, mapGetters } from "vuex";

export default {
  components: {
    InvoicePreviewDuplicate,
  },
  props: {
    invoice: {
      required: true,
      validator: (prop) => typeof prop === "object" || prop === null,
    },
  },
  data() {
    return {
      loading: true,
      duplicateInvoices: [],
      articleData: {},
      businessUnit: null,
      shown: false,
    };
  },
  computed: {
    ...mapGetters("auth", ["user"]),
    userTypes() {
      return this.$store.state.userTypes.items;
    },
    company() {
      return this.$store.state.companies.myCompany;
    },
    users() {
      return this.$store.state.users.items
    },
    invoiceHeader() {
      return this.$store.state.devices.invoiceHeader;
    },
    invoiceFooter() {
      return this.$store.state.devices.invoiceFooter;
    },
  },
  methods: {
    ...mapActions("companies", [
      "getClient",
      "invoiceDuplicates",
      "loadMyCompany",
      "vrniCompanyIdentity",
      "handleInvoiceDuplicate"
    ]),
    ...mapActions("articles", ["loadItemWithHeaders"]),
    ...mapActions("devices", ["getHeader", "getFooter"]),
    ...mapActions("invoices", ["getPrints"]),
    ...mapActions("users", ["loadUsersPerHeader"]),
    ...mapActions("businessUnits", { loadBusinessUnits: "loadItems" }),
    ...mapActions("userTypes", ["loadItems"]),
    async open(errorData) {
      this.duplicateInvoices = []
      if (!this.invoice.duplicate_invoice || !errorData) return;
      this.loading = true;
      let takenCompany = await this.vrniCompanyIdentity(errorData.company, false)
      const headers = {
        Authorization: `Bearer ${takenCompany.token}`,
      };
      const requests = [
        this.getHeader({
          electronicDeviceId: errorData.electronic_device,
          headers,
        }),
        this.getFooter({
          electronicDeviceId: errorData.electronic_device,
          headers,
        }),
        this.invoiceDuplicates(this.invoice.duplicate_invoice),
        this.loadBusinessUnits(headers),
        this.loadUsersPerHeader(headers)
        //this.getPrints(this.invoice)
      ];
      let returns = await Promise.all(requests)
      this.businessUnit = returns[3][errorData.business_unit]
      for (const nr in returns[2]) {
        let duplicate = returns[2][nr];
        let duplicateData = JSON.parse(duplicate.data)
        duplicateData.duplicateId = duplicate.id
        let fullInvoice = await this.addDataToInvoice(errorData, duplicateData, headers)
        this.duplicateInvoices.push(fullInvoice)
      }
      this.loading = false;
      this.shown = true;
    },
    keep(item) {
      this.handleInvoiceDuplicate({ id: item.duplicateId, accept: true })
        .then(() => {
          this.shown = false
        })
    },
    cancel(item) {
      this.handleInvoiceDuplicate({ id: item.duplicateId, accept: false })
        .then(() => {
          this.shown = false
        })
    },
    addDataToInvoice(errorData, duplicateData, headers) {
      return new Promise((resolve, reject) => {
        let requests = [];
        for (const nr in duplicateData.articles) {
          let articleData = duplicateData.articles[nr]
          if (!this.articleData[articleData.article]) {
            requests.push(this.loadItemWithHeaders({ article: articleData.article, headers, businessUnitId: this.businessUnit.id }))
          }
        }
        Promise.all(requests).then((results) => {
          if (results.length) {
            this.articleData = { ...this.articleData, ...results.reduce((acc, item) => {
              let key = Object.keys(item)[0];
              acc[key] = item[key]
              return acc
            })}
          }
          const fullInvoice = { ...duplicateData };
          fullInvoice.electronic_device_id = errorData.electronic_device_id
          fullInvoice.moniker = this.users[duplicateData.issuer].moniker
          fullInvoice.payment_amount = duplicateData.invoice_amount
          fullInvoice.company = this.company;
          fullInvoice.businessUnit = this.businessUnit;
          fullInvoice.invoiceFullNum = invoiceFullNum(
            duplicateData,
            this.businessUnit,
            errorData
          );
          fullInvoice.formattedDate = moment(duplicateData.date).format(
            "DD.MM.YYYY HH:mm:ss"
          );
          fullInvoice.discountTotal = calculateTotalDiscount(
            duplicateData.articles
          );
          fullInvoice.qrCodeData = qrCodeString(
            duplicateData.zoi,
            duplicateData.date,
            this.company.tax_number
          );
          fullInvoice.taxGroups = taxGroups(duplicateData.articles, this.articleData);
          /* if (this.duplicateData.client) {
              fullInvoice.buyerInfo = results[3]
            } */
          fullInvoice.header = this.invoiceHeader;
          fullInvoice.footer = this.invoiceFooter;
          // fullInvoice.printDetails = results[0]
          /* fullInvoice.printNumber = getLastInvoiceCopyNumber(results[2]) */
          /* fullInvoice.invoiceCopyNumber = fullInvoice.printNumber */
          resolve(fullInvoice);
        });
      });
    },
  },
  created() {
    this.loadItems();
  },
};

const getLastInvoiceCopyNumber = (printDetails) => {
  if (!printDetails.length) return 1;
  const maxPrintNum = Math.max.apply(
    Math,
    printDetails.map((print) => {
      return print.index;
    })
  );
  return maxPrintNum + 1;
};

const calculateTotalDiscount = (articles) => {
  let discount = 0;
  articles.forEach((item) => {
    let itemPrice = item.price * item.quantity;
    if (item.discount) discount += itemPrice * (item.discount / 100);
  });
  return roundedFloat(discount);
};

const invoiceFullNum = (invoice, businessUnit, errorData) => {
  return `${businessUnit.business_premise_id}-${errorData.electronic_device_id}-${invoice.number}`;
};

const roundedFloat = (number) => {
  return Number.parseFloat(
    Number.parseFloat((number + "").replace(",", ".")).toFixed(2)
  );
};

const qrCodeString = (zoi, date, taxNum) => {
  const zoiDec = BigNumber(zoi, 16).toString(10);
  const formattedDate = moment(date).format("YYMMDDHHmmss");
  const firstPart = `${zoiDec}${taxNum}${formattedDate}`;
  let firstPartSum = 0;
  for (let i = 0; i < firstPart.length; i++) {
    let num = parseInt(firstPart[i], 10);
    firstPartSum += num;
  }
  const control = firstPartSum % 10;
  return `${firstPart}${control}`;
};

const taxGroups = (articles, articleData) => {
  const groups = {};
  articles.forEach((article) => {
    const key = articleData[article.article].tax_level_value.toString();
    if (groups[key]) {
      groups[key].taxable_amount += article.taxable_amount;
      groups[key].tax_amount += article.tax_amount;
      groups[key].price += article.total_amount;
    } else {
      groups[key] = {
        tax_level_value: articleData[article.article].tax_level_value,
        taxable_amount: article.taxable_amount,
        tax_amount: article.tax_amount,
        price: article.total_amount,
      };
    }
  });
  return Object.values(groups);
};
</script>

<style lang="scss" scoped>
.preview {
  padding-top: 15px;
  max-height: calc(90vh - 130px);
  overflow: auto;
  .invoice-component {
    margin: 0 auto;
  }
}
</style>
